import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CONTENT } from "@/constants/contentTestIds";

export default function ContentCategories() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(null);
  const [cats, setCats] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", description: "" });
  const [error, setError] = useState("");

  const load = async () => {
    const [{ data: b }, { data: cs }] = await Promise.all([
      api.get(`/brands/${brandId}`),
      api.get(`/brands/${brandId}/content-categories`),
    ]);
    setBrand(b);
    setCats(cs);
  };
  useEffect(() => { load(); }, [brandId]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post(`/brands/${brandId}/content-categories`, form);
      setCreating(false);
      setForm({ name: "", slug: "", description: "" });
      navigate(`/brands/${brandId}/content/${data.category_id}`);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not create category");
    }
  };

  return (
    <div data-testid={CONTENT.pageRoot} className="max-w-5xl">
      <div className="flex items-start justify-between mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#B5462F] mb-3">
            {brand?.name || "Brand"} &middot; Content Library
          </p>
          <h1 className="font-serif text-5xl tracking-tight">Content & Assets</h1>
          <p className="text-base text-[#3D3A35] mt-3 max-w-2xl">
            Define the categories of content your local marketers will choose from
            — Products, Promotions, and any future category. Each category has its
            own schema of named, typed fields. Items in the category fill those
            fields with values.
          </p>
        </div>
        {!creating && (
          <button
            data-testid={CONTENT.newCategoryButton}
            onClick={() => setCreating(true)}
            className="px-5 py-2.5 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm font-medium"
          >
            New category
          </button>
        )}
      </div>

      {creating && (
        <form onSubmit={submit} className="border border-[#1A1F2C]/10 bg-white rounded-lg p-6 mb-8">
          <h3 className="font-serif text-xl mb-4">New content category</h3>
          <div className="grid grid-cols-2 gap-4">
            <LField label="Name" testid={CONTENT.newCategoryName} value={form.name} onChange={(v) => setForm({ ...form, name: v, slug: form.slug || v.toLowerCase().replace(/[^a-z0-9]+/g, "_") })} required />
            <LField label="Slug (snake_case)" testid={CONTENT.newCategorySlug} value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} required />
          </div>
          <div className="mt-4">
            <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6358] mb-1">Description (optional)</label>
            <textarea
              data-testid={CONTENT.newCategoryDesc}
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
            />
          </div>
          {error && <p className="text-sm text-[#B5462F] mt-3">{error}</p>}
          <div className="flex gap-3 mt-5">
            <button type="submit" data-testid={CONTENT.newCategorySubmit} className="px-5 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm font-medium">Create category</button>
            <button type="button" data-testid={CONTENT.newCategoryCancel} onClick={() => setCreating(false)} className="px-5 py-2 text-sm text-[#6B6358]">Cancel</button>
          </div>
        </form>
      )}

      <div className="border border-[#1A1F2C]/10 bg-white rounded-lg divide-y divide-[#1A1F2C]/10">
        {cats.length === 0 && (
          <div className="px-6 py-8 text-sm text-[#6B6358]">No categories yet.</div>
        )}
        {cats.map((c) => (
          <Link
            key={c.category_id}
            to={`/brands/${brandId}/content/${c.category_id}`}
            data-testid={`${CONTENT.categoryRowPrefix}${c.category_id}`}
            className="block px-6 py-5 hover:bg-[#F8F5EE]"
          >
            <div className="flex items-baseline justify-between">
              <div>
                <div className="font-serif text-xl">{c.name}</div>
                <div className="text-xs text-[#6B6358] mt-1 font-mono">{c.slug}</div>
              </div>
              <div className="text-xs uppercase tracking-[0.15em] text-[#6B6358]">
                {c.fields?.length || 0} fields
              </div>
            </div>
            {c.description && (
              <p className="text-sm text-[#6B6358] mt-2">{c.description}</p>
            )}
          </Link>
        ))}
      </div>

      <div className="mt-10 text-sm text-[#6B6358] max-w-2xl">
        <p>
          Tip: The Brand Kit (logos, colors, fonts) will arrive in a follow-up
          milestone. For M2, brand-logo / wordmark fields on Promotion items are
          per-item images that can later be migrated to a Brand Kit reference.
        </p>
      </div>
    </div>
  );
}

function LField({ label, testid, value, onChange, required }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6358] mb-1">{label}</label>
      <input
        data-testid={testid}
        type="text"
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
      />
    </div>
  );
}
